import {
  UserOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, notification, Radio, Rate, Select, Spin, Tag } from "antd";
import { FieldDisplayDumb } from "../FieldDisplayDumb";
import { PersonalDataDetailsPanelProps, StudentPersonalDetailsType } from "./types";
import axiosInstance from "../../axios";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import ReactGoogleAutocomplete from "react-google-autocomplete";
import { ActionForNotification, NotificationType, popupTitleAndMessage } from "../utils/notificationPopup";
import { adminEditDealState, userRole, userStudentCityAccess } from "../../states/userRole";
import { useAtom } from "jotai";
import { USER_ROLES } from "../../constants/roleConstants";
import { dateDisplayable, dateTimeDisplayable } from "../table-utils";
const apiKey = (window._env_ && window._env_.REACT_APP_GOOGLE_MAPS_API_KEY) || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

type GoogleAddressComponent = { long_name: string; short_name: string; types: string[] };

type GooglePlaceResult = {
  formatted_address?: string;
  address_components?: GoogleAddressComponent[];
  geometry?: { location: { lat: () => number; lng: () => number } };
};

const getAddressComponent = (components: GoogleAddressComponent[], type: string) => {
  const comp = components.find((c) => c.types.includes(type));
  return comp ? comp.long_name : "";
};

const resolveCityIdFromPlace = (components: GoogleAddressComponent[], citys: { [key: number]: string }) => {
  const cityName =
    getAddressComponent(components, "locality") ||
    getAddressComponent(components, "administrative_area_level_2");
  if (!cityName) return undefined;
  const match = Object.entries(citys).find(([, name]) => name?.toLowerCase() === cityName.toLowerCase());
  return match ? Number(match[0]) : undefined;
};

type LocationAutocompleteProps = {
  apiKey: string;
  initialAddress: string;
  citys: { [key: number]: string };
  onAddressChange: (updates: {
    location: string;
    latitude?: number;
    longitude?: number;
    gmap_meta_data?: string;
    city_id?: number;
  }) => void;
};

/** Uncontrolled input — Google Places breaks when `value` is controlled. */
function StudentLocationAutocomplete({ apiKey, initialAddress, citys, onAddressChange }: LocationAutocompleteProps) {
  return (
    <ReactGoogleAutocomplete
      apiKey={apiKey}
      libraries={["places"]}
      inputAutocompleteValue="off"
      placeholder="Search address on Google Maps"
      className="ant-input ant-input-outlined tail-w-full"
      defaultValue={initialAddress}
      style={{
        width: "100%",
        padding: "4px 11px",
        minHeight: "32px",
        border: "1px solid #d9d9d9",
        borderRadius: "6px",
        backgroundColor: "#ffffff",
        cursor: "text",
      }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        onAddressChange({ location: e.target?.value ?? "" });
      }}
      onPlaceSelected={(place: GooglePlaceResult) => {
        if (!place?.geometry?.location) return;

        const addressComponents = place.address_components ?? [];
        const formattedAddress =
          place.formatted_address || addressComponents.map((c) => c.long_name).join(", ");
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        const matchedCityId = resolveCityIdFromPlace(addressComponents, citys);

        onAddressChange({
          location: formattedAddress,
          latitude,
          longitude,
          gmap_meta_data: JSON.stringify(place),
          ...(matchedCityId != null ? { city_id: matchedCityId } : {}),
        });
      }}
      options={{
        types: [],
        componentRestrictions: { country: "IN" },
        fields: ["address_components", "geometry", "formatted_address", "place_id"],
      }}
    />
  );
}

export function PersonalDataDetailsPanel(props: PersonalDataDetailsPanelProps) {
  const { citys, studentDetails } = props;
  const [spinning, setSpinning] = useState(false);
  const [NotificationInstanceApi, NotificationInstanceContextHolder] = notification.useNotification();
  const { subscriptionStatus, setSubscriptionStatus, setStudentDetails } = props;
  const [rating, setRating] = useState<number>(studentDetails?.rating ?? 0);
  const [disableRating, setDisableRating] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState<Partial<StudentPersonalDetailsType> | null>(studentDetails);
  const [address, setAddress] = useState(studentDetails?.location);
  const [locationEditKey, setLocationEditKey] = useState(0);
  const [isPaymentTypeModalOpen, setIsPaymentTypeModalOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<"basic" | "premium">("basic");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [role, _setRole] = useAtom<string>(userRole);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [studentCityAccess, _setStudentCityAccess] = useAtom(userStudentCityAccess);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canAdminEditDeal, _setCanAdminEditDeal] = useAtom(adminEditDealState);

  useEffect(() => {
    getStudentSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditing) return;
    setInputValue(studentDetails);
    setAddress(studentDetails?.location ?? "");
  }, [studentDetails, isEditing]);

  const beginEditing = () => {
    setInputValue(studentDetails);
    setAddress(studentDetails?.location ?? "");
    setLocationEditKey((k) => k + 1);
    setIsEditing(true);
  };

  const applyLocationUpdate = (updates: {
    location: string;
    latitude?: number;
    longitude?: number;
    gmap_meta_data?: string;
    city_id?: number;
  }) => {
    setAddress(updates.location);
    setInputValue((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  async function updateCustomerRating(newRating: number) {
    try {
      const { data } = await axiosInstance.post("/v1/cre/student/student-rating", {
        student_id: studentDetails.studentId,
        rating: newRating,
      });
      if (data.success) {
        setRating(newRating);
        openNotificationWithIcon("success", "UPDATE_STUDENT_RATING");
      }
      setDisableRating(true);
    } catch (error) {
      openNotificationWithIcon("error", "UPDATE_STUDENT_RATING");
    }
    setSpinning(false);
  }

  async function getStudentSubscriptionStatus() {
    const { data } = await axiosInstance.get("/v1/cre/student/subscription-status", {
      params: { student_id: studentDetails.studentId },
    });
    if (data.success) {
      setSubscriptionStatus(data.data);
    }
  }

  async function subscribeUnsubscribeStudent(paymentType?: "basic" | "premium") {
    try {
      const { data } = await axiosInstance.post("/v1/cre/student/subscribe-unsubscribe", {
        student_id: studentDetails.studentId,
        paymentType: paymentType,
      });
      if (data.success) {
        setSubscriptionStatus(data.data);
        openNotificationWithIcon(
          "success",
          "UPDATE_STUDENT_SUBSCRIPTION",
          data.data?.status,
          `Successfully ${data.data?.status} Student`,
        );
      }
    } catch (error) {
      openNotificationWithIcon("error", "UPDATE_STUDENT_SUBSCRIPTION");
    }
    setSpinning(false);
    setIsPaymentTypeModalOpen(false);
  }

  const handleSubscribeClick = () => {
    if (subscriptionStatus?.status === "Subscribed") {
      // If already subscribed, directly unsubscribe
      setSpinning(true);
      subscribeUnsubscribeStudent();
    } else {
      // If not subscribed, open modal to select plan
      setIsPaymentTypeModalOpen(true);
    }
  };

  const handlePaymentTypeProceed = () => {
    setSpinning(true);
    subscribeUnsubscribeStudent(selectedPaymentType);
  };

  async function toggleJobsPortalPublish(publish: boolean) {
    try {
      const { data } = await axiosInstance.post("/v1/cre/job-deals/toggle", {
        dealings_id: studentDetails.id,
        publish,
      });
      if (data.success) {
        setStudentDetails((prev) =>
          prev
            ? {
                ...prev,
                is_published_to_jobs: publish,
                job_deal_id: data.data?.id ?? null,
                job_published_at: data.data?.published_at ?? null,
              }
            : prev,
        );
        openNotificationWithIcon(
          "success",
          "STORE_STUDENT_PERSONAL_DETAIL",
          publish ? "Published to Jobs Portal" : "Removed from Jobs Portal",
          data.message,
        );
      } else {
        openNotificationWithIcon("error", "STORE_STUDENT_PERSONAL_DETAIL", undefined, data.message);
      }
    } catch (error) {
      openNotificationWithIcon("error", "STORE_STUDENT_PERSONAL_DETAIL", undefined, "Failed to update jobs portal status");
    }
    setSpinning(false);
  }

  const hasStudentCityAccess = Boolean(
    studentCityAccess.find((cityAccess) => String(cityAccess) === String(studentDetails.city_id)),
  );
  const canPublishToJobsPortal =
    role === USER_ROLES.SUPERADMIN ||
    ((role === USER_ROLES.ADMIN || role === USER_ROLES.SUPERVISER) && hasStudentCityAccess);

  const parseGmapMetaForApi = (raw: StudentPersonalDetailsType["gmap_meta_data"] | undefined) => {
    if (!raw) return [];
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  async function updateStudentDetail() {
    try {
      const { data } = await axiosInstance.post("/v1/cre/student/detail", {
        ...inputValue,
        location: address ?? inputValue?.location,
        gmap_meta_data: parseGmapMetaForApi(inputValue?.gmap_meta_data),
        dealings_id: studentDetails.id,
      });

      if (data.success && data?.data) {
        setStudentDetails(data?.data);
        setIsEditing(false);
        openNotificationWithIcon("success", "STORE_STUDENT_PERSONAL_DETAIL");
      } else {
        setInputValue(studentDetails);
        setAddress(studentDetails.location);
        openNotificationWithIcon("error", "STORE_STUDENT_PERSONAL_DETAIL", undefined, data.message);
      }
    } catch (error) {
      setInputValue(studentDetails);
      setAddress(studentDetails.location);
      openNotificationWithIcon("error", "STORE_STUDENT_PERSONAL_DETAIL");
    }
    setSpinning(false);
  }

  const openNotificationWithIcon = (
    type: NotificationType,
    action: keyof typeof ActionForNotification,
    message?: string,
    description?: string,
  ) => {
    NotificationInstanceApi[type]({
      message: message ?? popupTitleAndMessage[action][type].title,
      description: description ?? popupTitleAndMessage[action][type].description,
      placement: "bottomRight",
    });
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <div className="tail-p-4 tail-border tail-rounded-lg tail-border-zinc-300 tail-shadow-md">
        {NotificationInstanceContextHolder}
        {/* Header Section */}
        <div className="tail-flex tail-justify-between tail-items-center tail-mb-4">
          <div className="tail-text-blue-500 tail-flex tail-items-center tail-gap-2">
            <UserOutlined className="tail-text-2xl" />
            <span className="tail-text-lg tail-font-medium">Customer Details</span>
          </div>
          <div className="tail-text-right">
            <Tag
              color={subscriptionStatus?.status === "Subscribed" ? "success" : "error"}
              className="tail-mb-2 tail-py-2"
            >
              {subscriptionStatus?.status}{" "}
              {subscriptionStatus?.status === "Subscribed" ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
            </Tag>
            <p className="tail-text-xs tail-text-gray-500 tail-pb-2">
              Expired on:{" "}
              {subscriptionStatus?.expiry_at ? dayjs(subscriptionStatus?.expiry_at).format("Do MMM YYYY") : ""}
            </p>
            <Button
              // disabled={!canAdminEditDeal}
              disabled={
                role !== USER_ROLES.SUPERADMIN &&
                !(
                  role === USER_ROLES.ADMIN &&
                  studentCityAccess.find((cityAccess) => String(cityAccess) === String(studentDetails.city_id))
                )
              }
              onClick={handleSubscribeClick}
              type="primary"
              size="large"
            >
              {subscriptionStatus?.status === "Subscribed" ? "Unsubscribe" : "Subscribe"}
            </Button>
            <div className="tail-mt-3">
              <Checkbox
                checked={Boolean(studentDetails?.is_published_to_jobs)}
                disabled={!canPublishToJobsPortal}
                onChange={(e) => {
                  setSpinning(true);
                  toggleJobsPortalPublish(e.target.checked);
                }}
              >
                Publish to Jobs Portal
              </Checkbox>
            </div>
          </div>
        </div>

        {isEditing ? (
          <div className="tail-grid tail-grid-cols-2 tail-gap-4">
            {[
              {
                component: "input",
                key: "full_name",
                label: "Full Name",
                value: `${inputValue?.first_name} ${inputValue?.last_name}`,
              },
              {
                component: "input",
                key: "phone2",
                label: "Alternate Phone",
                value: inputValue?.phone2,
                addonBefore: "+91",
              },
              {
                component: "select",
                key: "gender",
                label: "Gender",
                value: inputValue?.gender,
                options: [
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "both", label: "Both" },
                ],
              },
              {
                component: "input",
                key: "email",
                label: "Email",
                value: inputValue?.email,
              },
              {
                component: "input",
                key: "flat",
                label: "Flat",
                value: inputValue?.flat,
              },
              {
                component: "input",
                key: "apartment",
                label: "Apartment",
                value: inputValue?.apartment,
              },
              {
                component: "input",
                key: "street",
                label: "Street",
                value: inputValue?.street,
              },
            ].map((field, index) => (
              <div key={index}>
                <label className="tail-block tail-text-sm tail-font-medium">{field.label}</label>
                {field.component === "select" ? (
                  <Select
                    defaultValue={field.value}
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      setInputValue({
                        ...inputValue,
                        [field.key]: value,
                      });
                    }}
                    options={field.options}
                  />
                ) : (
                  <Input
                    value={field.value}
                    addonBefore={field.addonBefore}
                    onChange={(e) => {
                      setInputValue({
                        ...inputValue,
                        [field.key]: e.target.value,
                      });
                    }}
                    className="tail-w-full"
                  />
                )}
              </div>
            ))}

            <div>
              <label className="tail-block tail-text-sm tail-font-medium">City</label>
              <Select
                defaultValue={inputValue?.city_id ? citys[inputValue.city_id] : ""}
                style={{ width: "100%" }}
                onChange={(value) => {
                  setInputValue({
                    ...inputValue,
                    city_id: Number(value),
                  });
                }}
                options={Object.entries(citys).map(([key, value]) => ({
                  key,
                  value: key,
                  label: value,
                }))}
              />
            </div>

            <div className="tail-col-span-2">
              <label className="tail-block tail-text-sm tail-font-medium">Location</label>
              {apiKey ? (
                <StudentLocationAutocomplete
                  key={`student-location-${studentDetails.studentId}-${locationEditKey}`}
                  apiKey={apiKey}
                  initialAddress={studentDetails?.location ?? ""}
                  citys={citys}
                  onAddressChange={applyLocationUpdate}
                />
              ) : (
                <Input
                  value={address ?? ""}
                  placeholder="Enter address (Google Maps API key not configured)"
                  className="tail-w-full"
                  onChange={(e) => applyLocationUpdate({ location: e.target.value })}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="tail-grid tail-grid-cols-2 tail-gap-4">
            <FieldDisplayDumb head="Name" val={`${studentDetails?.first_name} ${studentDetails?.last_name}`} />
            <FieldDisplayDumb head="Phone" val={`+91 ${studentDetails?.phone}`} />
            <FieldDisplayDumb
              head="Alternate Phone"
              val={studentDetails?.phone2 ? `+91 ${studentDetails?.phone2}` : ""}
            />
            <FieldDisplayDumb head="Gender" val={studentDetails?.gender} />
            <FieldDisplayDumb head="E-mail ID" val={studentDetails?.email} />
            <FieldDisplayDumb head="City" val={citys[studentDetails?.city_id]} />
            <FieldDisplayDumb head="Platform" val={studentDetails?.platform} />
            <FieldDisplayDumb
              head="Flat/Apartment/Street"
              val={`${studentDetails?.flat ? `${studentDetails?.flat} / ` : ""}${
                studentDetails?.apartment ? `${studentDetails?.apartment} / ` : ""
              }${studentDetails?.street}`}
            />
            <FieldDisplayDumb head="Address" val={studentDetails?.location} />
            <FieldDisplayDumb
              head="Signup On"
              val={studentDetails?.created_at ? dateDisplayable(studentDetails?.created_at) : ""}
            />
            <FieldDisplayDumb
              head="Last Login"
              val={
                studentDetails?.last_login_at
                  ? dateTimeDisplayable(studentDetails.last_login_at)
                  : "Never"
              }
            />
          </div>
        )}

        {/* Rating & Edit Button */}
        <div className="tail-flex tail-flex-col tail-items-end tail-mt-4">
          <div className="tail-flex tail-items-center tail-gap-x-4 tail-mb-2">
            <span>Rating</span>
            <Button
              type={disableRating ? "text" : "primary"}
              size="small"
              variant="dashed"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => setDisableRating(!disableRating)}
            />
          </div>
          <Rate
            disabled={disableRating}
            allowHalf
            value={rating}
            className={`${disableRating ? "tail-text-xs" : "tail-text-xl"} !tail-text-amber-400`}
            onChange={(newRating) => {
              setSpinning(true);
              updateCustomerRating(newRating);
            }}
          />
          <div className="tail-flex tail-items-center tail-gap-2">
            <Button
              type="primary"
              icon={<EditOutlined />}
              iconPosition="end"
              size="middle"
              className="tail-mt-2"
              // disabled={!canAdminEditDeal}
              onClick={() => {
                if (isEditing) {
                  setSpinning(true);
                  updateStudentDetail();
                } else {
                  beginEditing();
                }
              }}
            >
              {isEditing ? "Save" : "Edit"}
            </Button>
            {isEditing && (
              <Button
                danger
                className="tail-mt-2"
                icon={<ClearOutlined />}
                iconPosition="end"
                size="middle"
                onClick={() => {
                  setInputValue(studentDetails);
                  setAddress(studentDetails?.location ?? "");
                  setLocationEditKey((k) => k + 1);
                  setIsEditing(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Payment Type Selection Modal */}
      <Modal
        title="Select Parents Type"
        open={isPaymentTypeModalOpen}
        onOk={handlePaymentTypeProceed}
        onCancel={() => {
          setIsPaymentTypeModalOpen(false);
          setSelectedPaymentType("basic");
        }}
        okText="Proceed"
        cancelText="Cancel"
      >
        <div style={{ padding: "20px 0" }}>
          <Radio.Group
            onChange={(e) => setSelectedPaymentType(e.target.value)}
            value={selectedPaymentType}
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <Radio value="basic" style={{ fontSize: "16px" }}>
              Normal Parents
            </Radio>
            <Radio value="premium" style={{ fontSize: "16px" }}>
              Shadow Parents
            </Radio>
          </Radio.Group>
        </div>
      </Modal>
    </>
  );
}
